/**
 * Decision Support service for managing chat sessions
 */

import authService from "./auth";

const API_BASE_URL = import.meta.env.VITE_API_BASE_URL;

export interface ChatSession {
  id: number;
  title: string;
  analysis_type: string;
  starting_prompt: string;
  files_metadata: Array<{
    filename: string;
    size: number;
    type: string;
  }>;
  created_at: string;
  updated_at?: string;
  last_message_at: string;
  message_count: number;
}

export interface ChatMessage {
  id: number;
  role: "user" | "assistant";
  content: string;
  extra_data: {
    model_id?: string;
    input_tokens?: number;
    output_tokens?: number;
    total_tokens?: number;
    files_count?: number;
    rag_chunks?: Array<{
      content: string;
      source: string;
      page_number?: number;
      score: number;
      chunk_id: string;
    }>;
  };
  created_at: string;
}

export interface PromptInfo {
  analysis_type: string;
  name: string;
  icon: string;
  description: string;
  prompt: string;
}

/**
 * Fetch all available prompts from backend
 */
export async function getAllPrompts(): Promise<PromptInfo[]> {
  const token = authService.getAccessToken();
  const response = await fetch(`${API_BASE_URL}/api/decision-support/prompts`, {
    headers: {
      Authorization: `Bearer ${token}`,
    },
  });

  if (!response.ok) {
    throw new Error(`Failed to fetch prompts: ${response.statusText}`);
  }

  return response.json();
}

/**
 * Fetch a specific prompt by analysis type
 */
export async function getPromptByType(
  analysisType: string
): Promise<{ analysis_type: string; prompt: string }> {
  const token = authService.getAccessToken();
  const response = await fetch(
    `${API_BASE_URL}/api/decision-support/prompts/${analysisType}`,
    {
      headers: {
        Authorization: `Bearer ${token}`,
      },
    }
  );

  if (!response.ok) {
    throw new Error(`Failed to fetch prompt: ${response.statusText}`);
  }

  return response.json();
}

/**
 * Create a new chat session with streaming response
 */
export async function createSessionStream(
  title: string,
  analysisType: string,
  startingPrompt: string,
  files: File[],
  ragEnabled: boolean,
  ragNumChunks: number,
  ragMinScore: number,
  onContent: (content: string) => void,
  onSessionCreated: (sessionId: number, userMessage: ChatMessage) => void,
  onComplete: (session: ChatSession, messages: ChatMessage[]) => void,
  onError: (error: string) => void
): Promise<void> {
  const token = authService.getAccessToken();
  if (!token) {
    throw new Error("Not authenticated");
  }

  const formData = new FormData();
  formData.append("title", title);
  formData.append("analysis_type", analysisType);
  formData.append("starting_prompt", startingPrompt);
  formData.append("rag_enabled", String(ragEnabled));
  formData.append("rag_num_chunks", String(ragNumChunks));
  formData.append("rag_min_score", String(ragMinScore));

  // Append all files
  files.forEach((file) => {
    formData.append("files", file);
  });

  try {
    const response = await fetch(
      `${API_BASE_URL}/api/decision-support/sessions/stream`,
      {
        method: "POST",
        headers: {
          Authorization: `Bearer ${token}`,
        },
        body: formData,
      }
    );

    if (!response.ok) {
      const error = await response.json();
      onError(error.detail || "Failed to create session");
      return;
    }

    const reader = response.body?.getReader();
    const decoder = new TextDecoder();

    if (!reader) {
      onError("Unable to read stream");
      return;
    }

    let buffer = "";

    while (true) {
      const { done, value } = await reader.read();

      if (done) {
        break;
      }

      buffer += decoder.decode(value, { stream: true });
      const lines = buffer.split("\n");
      buffer = lines.pop() || "";

      for (const line of lines) {
        if (line.startsWith("data: ")) {
          const data = JSON.parse(line.substring(6));

          if (data.type === "session") {
            onSessionCreated(data.session_id, data.user_message);
          } else if (data.type === "content") {
            onContent(data.content);
          } else if (data.type === "done") {
            onComplete(data.session, data.messages);
          } else if (data.error) {
            onError(data.error);
            break;
          }
        }
      }
    }
  } catch (error) {
    onError(error instanceof Error ? error.message : "Unknown error");
  }
}

/**
 * Create a new chat session with initial analysis
 */
export async function createSession(
  title: string,
  analysisType: string,
  startingPrompt: string,
  files: File[]
): Promise<ChatSession> {
  const token = authService.getAccessToken();
  if (!token) {
    throw new Error("Not authenticated");
  }

  const formData = new FormData();
  formData.append("title", title);
  formData.append("analysis_type", analysisType);
  formData.append("starting_prompt", startingPrompt);

  // Append all files
  files.forEach((file) => {
    formData.append("files", file);
  });

  const response = await fetch(
    `${API_BASE_URL}/api/decision-support/sessions/streaming`,
    {
      method: "POST",
      headers: {
        Authorization: `Bearer ${token}`,
      },
      body: formData,
    }
  );

  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.detail || "Failed to create session");
  }

  return await response.json();
}

/**
 * Get all sessions for the current user
 */
export async function listSessions(): Promise<ChatSession[]> {
  const token = authService.getAccessToken();
  if (!token) {
    throw new Error("Not authenticated");
  }

  const response = await fetch(
    `${API_BASE_URL}/api/decision-support/sessions`,
    {
      headers: {
        Authorization: `Bearer ${token}`,
      },
    }
  );

  if (!response.ok) {
    throw new Error("Failed to fetch sessions");
  }

  return await response.json();
}

/**
 * Get a specific session
 */
export async function getSession(sessionId: number): Promise<ChatSession> {
  const token = authService.getAccessToken();
  if (!token) {
    throw new Error("Not authenticated");
  }

  const response = await fetch(
    `${API_BASE_URL}/api/decision-support/sessions/${sessionId}`,
    {
      headers: {
        Authorization: `Bearer ${token}`,
      },
    }
  );

  if (!response.ok) {
    throw new Error("Failed to fetch session");
  }

  return await response.json();
}

/**
 * Get all messages in a session
 */
export async function getSessionMessages(
  sessionId: number
): Promise<ChatMessage[]> {
  const token = authService.getAccessToken();
  if (!token) {
    throw new Error("Not authenticated");
  }

  const response = await fetch(
    `${API_BASE_URL}/api/decision-support/sessions/${sessionId}/messages`,
    {
      headers: {
        Authorization: `Bearer ${token}`,
      },
    }
  );

  if (!response.ok) {
    throw new Error("Failed to fetch messages");
  }

  return await response.json();
}

/**
 * Send a message in a session with streaming response
 */
export async function sendMessageStream(
  sessionId: number,
  content: string,
  onContent: (content: string) => void,
  onComplete: (userMessage: ChatMessage, assistantMessage: ChatMessage) => void,
  onError: (error: string) => void
): Promise<void> {
  const token = authService.getAccessToken();
  if (!token) {
    throw new Error("Not authenticated");
  }

  try {
    const response = await fetch(
      `${API_BASE_URL}/api/decision-support/sessions/${sessionId}/messages/stream`,
      {
        method: "POST",
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
        },
        body: JSON.stringify({ content }),
      }
    );

    if (!response.ok) {
      const error = await response.json();
      onError(error.detail || "Failed to send message");
      return;
    }

    const reader = response.body?.getReader();
    const decoder = new TextDecoder();

    if (!reader) {
      onError("Unable to read stream");
      return;
    }

    let buffer = "";

    while (true) {
      const { done, value } = await reader.read();

      if (done) {
        break;
      }

      buffer += decoder.decode(value, { stream: true });
      const lines = buffer.split("\n");
      buffer = lines.pop() || "";

      for (const line of lines) {
        if (line.startsWith("data: ")) {
          const data = JSON.parse(line.substring(6));

          if (data.type === "content") {
            onContent(data.content);
          } else if (data.type === "done") {
            onComplete(data.user_message, data.assistant_message);
          } else if (data.error) {
            onError(data.error);
            break;
          }
        }
      }
    }
  } catch (error) {
    onError(error instanceof Error ? error.message : "Unknown error");
  }
}

/**
 * Send a message in a session
 */
export async function sendMessage(
  sessionId: number,
  content: string
): Promise<ChatMessage> {
  const token = authService.getAccessToken();
  if (!token) {
    throw new Error("Not authenticated");
  }

  const response = await fetch(
    `${API_BASE_URL}/api/decision-support/sessions/${sessionId}/messages`,
    {
      method: "POST",
      headers: {
        Authorization: `Bearer ${token}`,
        "Content-Type": "application/json",
      },
      body: JSON.stringify({ content }),
    }
  );

  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.detail || "Failed to send message");
  }

  return await response.json();
}

/**
 * Delete a session
 */
export async function deleteSession(sessionId: number): Promise<void> {
  const token = authService.getAccessToken();
  if (!token) {
    throw new Error("Not authenticated");
  }

  const response = await fetch(
    `${API_BASE_URL}/api/decision-support/sessions/${sessionId}`,
    {
      method: "DELETE",
      headers: {
        Authorization: `Bearer ${token}`,
      },
    }
  );

  if (!response.ok) {
    throw new Error("Failed to delete session");
  }
}

/**
 * Update session title
 */
export async function updateSessionTitle(
  sessionId: number,
  title: string
): Promise<void> {
  const token = authService.getAccessToken();
  if (!token) {
    throw new Error("Not authenticated");
  }

  const formData = new FormData();
  formData.append("title", title);

  const response = await fetch(
    `${API_BASE_URL}/api/decision-support/sessions/${sessionId}/title`,
    {
      method: "PATCH",
      headers: {
        Authorization: `Bearer ${token}`,
      },
      body: formData,
    }
  );

  if (!response.ok) {
    throw new Error("Failed to update session title");
  }
}
